<template>
  <div class="recommend-list" :class="`recommend-list--${variant}`">
    <!--
      規約注記は「表示中の提供元」だけを列挙する。提供元ごとに情報の渡り先が
      違うため、枠に1社分だけ書くと不正確になる。
    -->
    <p v-if="visibleProviders.length > 0" class="recommend-list__note">
      Yagishの
      <a
        href="/terms"
        target="_blank"
        rel="noopener"
        class="recommend-list__link"
        >利用規約</a
      >、
      <a
        href="https://www.yagish.jp/privacy/"
        target="_blank"
        rel="noopener"
        class="recommend-list__link"
        >個人情報保護方針</a
      >、
      <a
        href="https://www.yagish.jp/privacy_policy/"
        target="_blank"
        rel="noopener"
        class="recommend-list__link"
        >プライバシーポリシー</a
      >、
      <template v-for="(provider, index) in visibleProviders">
        <span :key="`note-${provider}`">
          {{ providerLabel(provider) }}の<a
            :href="providerPrivacyUrl(provider)"
            target="_blank"
            rel="noopener"
            class="recommend-list__link"
            >プライバシーポリシー</a
          ><template v-if="index < visibleProviders.length - 1">、</template>
        </span>
      </template>
      に同意の上ご利用ください
    </p>

    <div
      v-for="block in visibleBlocks"
      :key="`${block.provider}-${block.blockIndex}`"
      class="recommend-list__block"
    >
      <div class="recommend-list__items">
        <article
          v-for="offer in block.offers"
          :key="`${offer.provider}-${offer.job_id}`"
          class="recommend-card"
        >
          <p v-if="employmentLabel(offer)" class="recommend-card__employ">
            {{ employmentLabel(offer) }}
          </p>

          <h4 class="recommend-card__title">{{ offer.title }}</h4>

          <div class="recommend-card__meta">
            <p v-if="offer.company_name" class="recommend-card__company">
              {{ offer.company_name }}
            </p>
            <p v-if="offer.location_text" class="recommend-card__area">
              {{ offer.location_text }}
            </p>
            <p v-if="offer.salary_text" class="recommend-card__salary">
              {{ truncate(offer.salary_text, 50) }}
            </p>
          </div>

          <div class="recommend-card__actions">
            <button
              type="button"
              class="recommend-card__detail"
              @click="onDetail(offer)"
            >
              <span>詳細を見る</span>
            </button>
            <!--
              apply_enabled が false の提供元は応募ボタンを出さない。
              本番の Carat は応募APIが止まっており、出すと必ず失敗するため。
              その場合は詳細モーダルの提供元サイトリンクが送客経路になる。
            -->
            <button
              v-if="offer.apply_enabled !== false && !offer.is_applied"
              type="button"
              class="recommend-card__apply"
              :disabled="isApplying(offer)"
              @click="onApply(offer)"
            >
              <span v-if="isApplying(offer)">送信中…</span>
              <span v-else>{{ providerApplyLabel(offer.provider) }}</span>
            </button>
            <button
              v-else-if="offer.is_applied"
              type="button"
              class="recommend-card__apply is-applied"
              disabled
            >
              <span>応募済み</span>
            </button>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import providersMaster from '@/assets/recommend/providers.json'
import type {
  PositionedOffer,
  RecommendBlock
} from '@/composables/useRecommendJobBlocks'

interface ProviderMaster {
  label: string
  privacy_url: string
  apply_label: string
}

const props = withDefaults(
  defineProps<{
    blocks: RecommendBlock[]
    /** grid = マイページのタイル / list = ダイアログの縦1列 */
    variant?: 'grid' | 'list'
    /** 応募完了画面のように、応募済みを並べたくない場面で true */
    hideApplied?: boolean
    /** 送信中の求人キー（`provider-job_id`）。該当カードの応募ボタンを止める */
    applyingKey?: string
  }>(),
  {
    variant: 'grid',
    hideApplied: false,
    applyingKey: ''
  }
)

const emit = defineEmits<{
  (e: 'detail', offer: PositionedOffer): void
  (e: 'apply', offer: PositionedOffer): void
}>()

const master = providersMaster as Record<string, ProviderMaster>

/** hideApplied のときは応募済みを落とし、空になったブロックも消す */
const visibleBlocks = computed<RecommendBlock[]>(() => {
  const blocks = props.blocks || []
  if (!props.hideApplied) return blocks
  return blocks
    .map((block) => ({
      ...block,
      offers: (block.offers || []).filter((offer) => !offer.is_applied)
    }))
    .filter((block) => block.offers.length > 0)
})

/** 表示中の提供元（重複なし・ブロック順） */
const visibleProviders = computed(() => {
  const seen: string[] = []
  for (const block of visibleBlocks.value) {
    if (block && block.provider && seen.indexOf(block.provider) < 0) {
      seen.push(block.provider)
    }
  }
  return seen
})

function providerLabel(provider: string): string {
  return (master[provider] && master[provider].label) || provider
}

function providerPrivacyUrl(provider: string): string {
  return (master[provider] && master[provider].privacy_url) || ''
}

function providerApplyLabel(provider: string): string {
  return (master[provider] && master[provider].apply_label) || '応募する'
}

function isApplying(offer: PositionedOffer): boolean {
  if (!props.applyingKey) return false
  return props.applyingKey === `${offer.provider}-${offer.job_id}`
}

function employmentLabel(offer: PositionedOffer): string {
  const types = offer.employment_types
  if (!Array.isArray(types) || types.length === 0) return ''
  return types.filter(Boolean).join(' / ')
}

function truncate(text: string, maxLength: number): string {
  if (!text) return ''
  const flat = text.replace(/\s+/g, ' ').trim()
  if (flat.length <= maxLength) return flat
  return flat.slice(0, maxLength) + '…'
}

function onDetail(offer: PositionedOffer) {
  emit('detail', offer)
}

function onApply(offer: PositionedOffer) {
  emit('apply', offer)
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables';

.recommend-list {
  &__note {
    opacity: 0.8;
    font-size: 12px;
    margin-bottom: 12px;
    text-align: left;
  }

  &__link {
    color: #e95b10;
  }

  &__block {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.recommend-card {
  background: #fff;
  border: 1px solid #dfdfdf;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  text-align: left;

  &__employ {
    background: #0f9934;
    color: #fff;
    border-radius: 50px;
    padding: 2px 10px;
    font-size: 0.6rem;
    align-self: flex-start;
    margin-bottom: 6px;
  }

  &__title {
    color: #004d40;
    line-height: 1.4;
    font-size: 0.95rem;
    font-weight: bold;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    margin-bottom: 8px;
  }

  &__meta {
    flex: 1;
    margin-bottom: 12px;
  }

  &__company {
    font-size: 0.8rem;
    font-weight: bold;
    margin-bottom: 4px;
  }

  &__area {
    position: relative;
    padding-left: 22px;
    font-size: 0.8rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;

    &::before {
      position: absolute;
      content: '';
      background-image: url('../../../public/static/renew3_img/txt_area.svg');
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      width: 14px;
      height: 14px;
      left: 0;
      top: calc(50% - 7px);
    }
  }

  &__salary {
    font-size: 0.8rem;
    opacity: 0.7;
    margin-top: 4px;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__detail {
    width: 100%;
    font-weight: bold;
    font-size: 0.875rem;
    background: #fff;
    padding: 10px;
    border: 1px solid #e3165c;
    border-radius: 6px;
    color: #e3165c;
  }

  &__apply {
    width: 100%;
    font-weight: bold;
    font-size: 0.875rem;
    background: #e3165c;
    border: 1px solid #e3165c;
    border-radius: 6px;
    padding: 10px 0;
    color: #fff;

    &.is-applied {
      background: #bbb;
      border-color: #bbb;
    }
  }
}

/* マイページ: 提供元ごとに独立したグリッド。列数が変わっても行内で混ざらない */
.recommend-list--grid {
  .recommend-list__items {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    @include max-width(sm) {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
  }
}

/* ダイアログ: 縦1列 */
.recommend-list--list {
  .recommend-list__items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .recommend-card {
    padding: 12px 16px;
  }
}
</style>
